import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  AlertCircle,
  Plus,
  X,
  UserCheck,
  Send,
  ClipboardCheck,
  BadgeCheck,
  Eye,
  ArrowUpRight,
  History,
  Download,
  FileText,
  Pencil,
  Search,
  ChevronDown,
} from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { useNCRs } from "../../../hooks/useNCRs";
import {
  inspectionsAPI,
  ncrAPI,
  projectsAPI,
  usersAPI,
} from "../../../services/api";
import NCRForm from "../../modals/forms/NCRForm";
import { normProjectRole, ROLE_GROUPS } from "../../../constants/projectRoles";
import { isSystemAdmin } from "../../../constants/systemRoles";

const EMPTY_FORM = {
  title: "",
  description: "",
  severity: "medium",
  status: "open",
  location: "",
  inspection_id: "",
};

const NCR_ASSIGNABLE_ROLES = new Set([
  "supervisor",
  "operations_manager",
]);
const NCR_ASSIGNER_ROLES = new Set([
  "supervisor",
  "operations_manager",
  "qa_qc_manager",
  "project_manager",
]);
const NCR_ESCALATION_ROLES = new Set([
  "foreman",
  "general_foreman",
  "project_engineer",
]);

const statusLabel = (s) => (s || "-").toString();
const isStatus = (s, v) => (s || "").toLowerCase() === v;
const isOneOf = (s, set) => set.has((s || "").toLowerCase());

const isAdmin = (systemRole) => isSystemAdmin(systemRole);
const canQAOrPM = (projectRole, systemRole) =>
  isAdmin(systemRole) ||
  ROLE_GROUPS.QA_OR_PM_OR_INSPECTOR.has(normProjectRole(projectRole));

const canAssign = (status, projectRole, systemRole) =>
  isStatus(status, "open") &&
  (isAdmin(systemRole) ||
    NCR_ASSIGNER_ROLES.has(normProjectRole(projectRole)));

const canEditNCR = (projectRole, systemRole) =>
  isAdmin(systemRole) || normProjectRole(projectRole) === "qa_qc_manager";

const canRespond = (detail, projectRole, systemRole, currentUserId) => {
  if (isAdmin(systemRole)) return false;
  if (!isStatus(detail?.status, "assigned")) return false;
  return Number(detail?.assigned_to_user_id) === Number(currentUserId);
};

const canResubmit = (detail, projectRole, systemRole, currentUserId) => {
  if (isAdmin(systemRole)) return false;
  if (!isStatus(detail?.status, "rejected")) return false;
  return Number(detail?.assigned_to_user_id) === Number(currentUserId);
};

const canStartReview = (status, projectRole, systemRole) =>
  canQAOrPM(projectRole, systemRole) &&
  isOneOf(status, new Set(["contractor_responded", "resubmitted"]));

const canReject = (status, projectRole, systemRole) =>
  canQAOrPM(projectRole, systemRole) && isStatus(status, "qa_review");

const canClose = (status, projectRole, systemRole) =>
  canQAOrPM(projectRole, systemRole) && isStatus(status, "qa_review");

const canCreateNCR = (projectRole, systemRole) => {
  if (isAdmin(systemRole)) return true;
  const pr = normProjectRole(projectRole);
  return ROLE_GROUPS.FIELD_CREATORS.has(pr);
};

const Pill = ({ text }) => {
  const v = (text || "").toLowerCase();
  const cls =
    v === "closed"
      ? "bg-green-100 text-green-700"
      : v === "qa_review"
        ? "bg-blue-100 text-blue-700"
        : v === "contractor_responded"
          ? "bg-purple-100 text-purple-700"
          : v === "resubmitted"
            ? "bg-indigo-100 text-indigo-700"
            : v === "assigned"
              ? "bg-amber-100 text-amber-700"
              : v === "rejected"
                ? "bg-red-100 text-red-700"
                : "bg-gray-100 text-gray-700";

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
    >
      {statusLabel(text)}
    </span>
  );
};

function SearchableTeamMemberSelect({
  options,
  value,
  onChange,
  getLabel,
  disabled,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedId = value ? Number(value) : null;

  useEffect(() => {
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = useMemo(
    () =>
      (options || []).find(
        (member) => Number(member.user_id) === selectedId,
      ) || null,
    [options, selectedId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((member) =>
      [getLabel(member), member.role]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [options, query, getLabel]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex min-h-[42px] w-full items-center justify-between gap-2 rounded border bg-white px-3 py-2 text-left text-sm disabled:opacity-60"
      >
        <span className={selected ? "truncate text-gray-900" : "text-gray-400"}>
          {selected ? getLabel(selected) : "Search Supervisor or Operations Manager"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      {open ? (
        <div className="absolute z-40 mt-2 w-full overflow-hidden rounded-xl border bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, email, or role..."
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                No matching eligible team member.
              </div>
            ) : (
              filtered.map((member) => (
                <button
                  key={member.id || member.user_id}
                  type="button"
                  onClick={() => {
                    onChange(String(member.user_id));
                    setQuery("");
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    Number(member.user_id) === selectedId ? "bg-orange-50" : ""
                  }`}
                >
                  <div className="truncate font-medium text-gray-900">
                    {getLabel(member)}
                  </div>
                  <div className="mt-0.5 text-xs capitalize text-gray-500">
                    {String(member.role || "").replaceAll("_", " ")}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const NCRView = () => {
  const { activeProject, projectRole, currentUser } = useProjectContext();
  const systemRole = (currentUser?.role || "").toLowerCase();
  const projectId = activeProject?.id || null;

  const { ncrs, loading, creating, error, create, stats, reload, upsertLocal } =
    useNCRs();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uiError, setUiError] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [inspectionsLoading, setInspectionsLoading] = useState(false);

  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgItem, setMsgItem] = useState(null);

  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const [assignPayload, setAssignPayload] = useState({
    assigned_to: "",
    contractor: "",
    assigned_to_user_id: "",
    due_date: "",
  });

  const [qaReviewComments, setQaReviewComments] = useState("");
  const [resubmitText, setResubmitText] = useState("");
  const [responseText, setResponseText] = useState("");
  const [verificationNotes, setVerificationNotes] = useState("");

  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [ncrEscalations, setNcrEscalations] = useState([]);
  const [ncrEscalationInbox, setNcrEscalationInbox] = useState([]);
  const [escalationTargets, setEscalationTargets] = useState([]);
  const [escalationTargetUserId, setEscalationTargetUserId] = useState("");
  const [escalationReason, setEscalationReason] = useState("");
  const [escalationAnswer, setEscalationAnswer] = useState("");
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLabel, setPdfPreviewLabel] = useState("");
  const [pdfPreviewNcrId, setPdfPreviewNcrId] = useState(null);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setPdfPreviewLabel("");
    setPdfPreviewNcrId(null);
    setPdfError(null);
  };

  const openPdfPreview = async (ncrId, label = "NCR Report") => {
    if (!projectId || !ncrId) return;
    setPdfBusyId(ncrId);
    setPdfError(null);
    try {
      const res = await ncrAPI.reportPdf(projectId, ncrId, false);
      const blob = new Blob([res.data], { type: "application/pdf" });
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(URL.createObjectURL(blob));
      setPdfPreviewLabel(label);
      setPdfPreviewNcrId(ncrId);
    } catch (err) {
      setPdfError(err?.response?.data?.detail || "Failed to load NCR PDF preview.");
    } finally {
      setPdfBusyId(null);
    }
  };

  const downloadPdf = async (ncrId, label = "NCR") => {
    if (!projectId || !ncrId) return;
    setPdfBusyId(ncrId);
    setPdfError(null);
    try {
      const res = await ncrAPI.reportPdf(projectId, ncrId, true);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${String(label || `NCR-${ncrId}`).replace(/[^A-Za-z0-9._-]+/g, "-")}-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setPdfError(err?.response?.data?.detail || "Failed to download NCR PDF.");
    } finally {
      setPdfBusyId(null);
    }
  };

  const allowCreate = useMemo(
    () => canCreateNCR(projectRole, systemRole),
    [projectRole, systemRole],
  );
  const allowEdit = useMemo(
    () => canEditNCR(projectRole, systemRole),
    [projectRole, systemRole],
  );

  const escalationRole = normProjectRole(projectRole);
  const canParticipateInEscalation =
    NCR_ESCALATION_ROLES.has(escalationRole) || escalationRole === "supervisor";

  const openEscalationToMe = useMemo(
    () =>
      (ncrEscalations || []).find(
        (item) =>
          String(item.status || "").toLowerCase() === "open" &&
          Number(item.escalated_to_user_id) === Number(currentUser?.id),
      ) || null,
    [ncrEscalations, currentUser?.id],
  );

  const isAssignedCurrentUser =
    Number(detail?.assigned_to_user_id) === Number(currentUser?.id);

  useEffect(() => {
    const loadInbox = async () => {
      if (!projectId || !canParticipateInEscalation) {
        setNcrEscalationInbox([]);
        return;
      }
      try {
        const res = await ncrAPI.escalationInbox(projectId);
        setNcrEscalationInbox(Array.isArray(res.data) ? res.data : []);
      } catch {
        setNcrEscalationInbox([]);
      }
    };
    loadInbox();
  }, [projectId, canParticipateInEscalation]);

  useEffect(() => {
    const loadInspections = async () => {
      if (!projectId) {
        setInspections([]);
        return;
      }
      setInspectionsLoading(true);
      try {
        const res = await inspectionsAPI.listByProject(projectId);
        setInspections(Array.isArray(res.data) ? res.data : []);
      } catch {
        setInspections([]);
      } finally {
        setInspectionsLoading(false);
      }
    };
    loadInspections();
  }, [projectId]);

  // const onChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const toggleCreateForm = () => {
    setUiError(null);
    if (!allowCreate) {
      setShowForm(false);
      setUiError("You don’t have permission to create an NCR in this project.");
      return;
    }
    setShowForm((v) => !v);
  };

  const onSubmitCreate = async (e) => {
    e.preventDefault();
    setUiError(null);

    if (!allowCreate) {
      setShowForm(false);
      setUiError("You don’t have permission to create an NCR in this project.");
      return;
    }

    try {
      await create({
        ...form,
        inspection_id: form.inspection_id ? Number(form.inspection_id) : null,
      });

      setShowForm(false);
      setForm(EMPTY_FORM);
      reload?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setUiError("Access denied (403): You are not allowed to create NCRs.");
      } else {
        setUiError("Failed to create NCR. Please try again.");
      }
    }
  };

  const closeDrawer = () => {
    setOpenId(null);
    setDetail(null);
    setEditing(false);
    setEditForm(EMPTY_FORM);

    setTimeline([]);
    setTimelineError(null);

    setActionError(null);
    setAssignPayload({
      assigned_to: "",
      contractor: "",
      assigned_to_user_id: "",
      due_date: "",
    });

    setQaReviewComments("");
    setResubmitText("");
    setResponseText("");
    setVerificationNotes("");
    setNcrEscalations([]);
    setEscalationTargets([]);
    setEscalationTargetUserId("");
    setEscalationReason("");
    setEscalationAnswer("");

    setMsgOpen(false);
    setMsgItem(null);
  };

  const openDrawer = (ncr) => {
    setOpenId(ncr.id);
    setDetail(ncr);
    setEditing(false);
    setEditForm({
      title: ncr.title || "",
      description: ncr.description || "",
      severity: ncr.severity || "medium",
      status: ncr.status || "open",
      location: ncr.location || "",
      inspection_id: ncr.inspection_id ? String(ncr.inspection_id) : "",
    });

    setQaReviewComments("");
    setResubmitText("");
    setResponseText("");
    setVerificationNotes("");

    setAssignPayload({
      assigned_to: ncr.assigned_to || "",
      contractor: ncr.contractor || "",
      assigned_to_user_id: ncr.assigned_to_user_id
        ? String(ncr.assigned_to_user_id)
        : "",
      due_date: ncr.due_date || "",
    });
  };

  const startEditing = () => {
    if (!detail || !allowEdit) return;
    setEditForm({
      title: detail.title || "",
      description: detail.description || "",
      severity: detail.severity || "medium",
      status: detail.status || "open",
      location: detail.location || "",
      inspection_id: detail.inspection_id
        ? String(detail.inspection_id)
        : "",
    });
    setActionError(null);
    setEditing(true);
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    if (!projectId || !openId || !allowEdit || actionBusy) return;

    setActionBusy(true);
    setActionError(null);
    try {
      const res = await ncrAPI.update(projectId, openId, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        severity: editForm.severity,
        location: editForm.location?.trim() || null,
        inspection_id: editForm.inspection_id
          ? Number(editForm.inspection_id)
          : null,
      });
      setDetail(res.data);
      upsertLocal?.(res.data);
      setEditing(false);
      const [, timelineRes] = await Promise.all([
        reload?.(),
        ncrAPI.actions(projectId, openId),
      ]);
      setTimeline(Array.isArray(timelineRes?.data) ? timelineRes.data : []);
    } catch (error) {
      setActionError(error);
    } finally {
      setActionBusy(false);
    }
  };

  const getUserLabelFromTeam = useCallback(
    (member) => {
      const email = member?.email || member?.user?.email;
      const first = member?.first_name || member?.user?.first_name || "";
      const last = member?.last_name || member?.user?.last_name || "";
      const name = `${first} ${last}`.trim();

      if (email) return `${name || email} (${email})`;

      const uid = member?.user_id;
      const u = allUsers.find((x) => x.id === uid);
      if (u?.email) {
        const nm = `${u.first_name || ""} ${u.last_name || ""}`.trim();
        return `${nm || u.email} (${u.email})`;
      }

      return uid ? `User #${uid}` : "Unknown user";
    },
    [allUsers],
  );

  const assignedToLabel = useMemo(() => {
    const uid = detail?.assigned_to_user_id;
    if (!uid) return detail?.assigned_to || "-";
    const member = (teamMembers || []).find(
      (m) => Number(m.user_id) === Number(uid),
    );
    return member
      ? getUserLabelFromTeam(member)
      : detail?.assigned_to || `User #${uid}`;
  }, [
    detail?.assigned_to_user_id,
    detail?.assigned_to,
    teamMembers,
    getUserLabelFromTeam,
  ]);

  useEffect(() => {
    const run = async () => {
      if (!projectId || !openId) return;

      setTeamLoading(true);
      setTeamError(null);
      try {
        const res = await projectsAPI.getTeam(projectId);
        setTeamMembers(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setTeamError(e);
        setTeamMembers([]);
      } finally {
        setTeamLoading(false);
      }
    };

    const loadUsersIfAdmin = async () => {
      if (!isAdmin(systemRole)) return;
      try {
        const res = await usersAPI.list();
        setAllUsers(Array.isArray(res.data) ? res.data : []);
      } catch {
        setAllUsers([]);
      }
    };

    run();
    loadUsersIfAdmin();
  }, [projectId, openId, systemRole]);

  useEffect(() => {
    const run = async () => {
      if (!openId || !projectId) return;

      setDetailLoading(true);
      setTimelineLoading(true);
      setTimelineError(null);

      try {
        const d = await ncrAPI.getOne(projectId, openId);
        setDetail(d.data);
        upsertLocal?.(d.data);
      } catch {
      } finally {
        setDetailLoading(false);
      }

      try {
        const r = await ncrAPI.actions(projectId, openId);
        setTimeline(r.data || []);
      } catch (e) {
        setTimelineError(e);
      } finally {
        setTimelineLoading(false);
      }

      try {
        const [escRes, targetRes] = await Promise.all([
          ncrAPI.escalations(projectId, openId),
          canParticipateInEscalation
            ? ncrAPI.escalationTargets(projectId, openId)
            : Promise.resolve({ data: [] }),
        ]);
        setNcrEscalations(Array.isArray(escRes.data) ? escRes.data : []);
        setEscalationTargets(
          Array.isArray(targetRes.data) ? targetRes.data : [],
        );
      } catch {
        setNcrEscalations([]);
        setEscalationTargets([]);
      }
    };

    run();
  }, [openId, projectId, upsertLocal, canParticipateInEscalation]);

  const refreshTimeline = async () => {
    if (!openId || !projectId) return;
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const r = await ncrAPI.actions(projectId, openId);
      setTimeline(r.data || []);
    } catch (e) {
      setTimelineError(e);
    } finally {
      setTimelineLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!openId || !projectId) return;
    try {
      const d = await ncrAPI.getOne(projectId, openId);
      setDetail(d.data);
      upsertLocal?.(d.data);
    } catch {
      // keep existing
    }
  };

  const refreshEscalations = async () => {
    if (!openId || !projectId) return;
    try {
      const [escRes, inboxRes] = await Promise.all([
        ncrAPI.escalations(projectId, openId),
        canParticipateInEscalation
          ? ncrAPI.escalationInbox(projectId)
          : Promise.resolve({ data: [] }),
      ]);
      setNcrEscalations(Array.isArray(escRes.data) ? escRes.data : []);
      setNcrEscalationInbox(Array.isArray(inboxRes.data) ? inboxRes.data : []);
    } catch {
      // keep current escalation state
    }
  };

  const doEscalationAction = async (fn) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await fn();
      setEscalationTargetUserId("");
      setEscalationReason("");
      setEscalationAnswer("");
      await Promise.all([refreshEscalations(), refreshTimeline()]);
    } catch (e) {
      setActionError(e);
    } finally {
      setActionBusy(false);
    }
  };

  const doAction = async (fn) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await fn();

      setQaReviewComments("");
      setResubmitText("");
      setResponseText("");
      setVerificationNotes("");

      await refreshDetail();
      await reload?.();
      await refreshTimeline();
    } catch (e) {
      setActionError(e);
    } finally {
      setActionBusy(false);
    }
  };

  const severityCounts = useMemo(() => {
    const by = { high: 0, medium: 0, low: 0 };
    (ncrs || []).forEach((n) => {
      const s = (n.severity || "").toLowerCase();
      if (by[s] !== undefined) by[s] += 1;
    });
    return by;
  }, [ncrs]);

  if (!activeProject) {
    return (
      <div className="p-4 text-sm text-gray-600">
        Select a project to view NCRs.
      </div>
    );
  }

  const noteLocked =
    "Note: each submitted response is locked in the NCR timeline. The main NCR card shows the latest response only.";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Non-Conformance Reports (NCRs)
          </h2>
          <p className="text-gray-600">
            Track, assign, review, and close NCRs with QA/QC workflow
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Total: {stats.total} • Open: {stats.open} • Closed: {stats.closed} •
            High: {severityCounts.high}
            {canParticipateInEscalation
              ? ` • Escalated to me: ${ncrEscalationInbox.filter((item) => String(item.status || "").toLowerCase() === "open").length}`
              : ""}
          </p>
        </div>
        <AlertCircle className="w-8 h-8 text-red-500" />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Project: <strong>{activeProject.name}</strong>
        </div>

        <div className="flex flex-col items-end gap-1">
          <button
            onClick={toggleCreateForm}
            disabled={!allowCreate}
            title={
              allowCreate
                ? "Create a new NCR"
                : "You are not allowed to create NCRs in this project"
            }
            className={[
              "inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition",
              allowCreate
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-gray-200 text-gray-500 cursor-not-allowed",
            ].join(" ")}
          >
            <Plus className="w-4 h-4" />
            New NCR
          </button>

          {!allowCreate && (
            <div className="text-xs text-gray-500">
              No permission to create NCR.
            </div>
          )}
        </div>
      </div>

      {uiError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {uiError}
        </div>
      )}

      {showForm && allowCreate && (
        <NCRForm
          value={form}
          onChange={(field, value) =>
            setForm((p) => ({ ...p, [field]: value }))
          }
          onSubmit={onSubmitCreate}
          onCancel={() => setShowForm(false)}
          creating={creating}
          inspections={inspections}
          inspectionsLoading={inspectionsLoading}
        />
      )}

      {pdfError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {pdfError}
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b text-sm font-medium text-gray-800">
          NCR List
        </div>

        {loading && <div className="p-4 text-sm text-gray-500">Loading...</div>}

        {error && (
          <div className="p-4 text-sm text-red-600">Failed to load NCRs.</div>
        )}

        {!loading && !error && (ncrs || []).length === 0 && (
          <div className="p-4 text-sm text-gray-500">No NCRs yet.</div>
        )}

        {!loading && !error && (ncrs || []).length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left p-3">#</th>
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">Severity</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Due</th>
                  <th className="text-left p-3">Inspection</th>
                  <th className="text-right p-3">Report</th>
                </tr>
              </thead>
              <tbody>
                {ncrs.map((n) => (
                  <tr
                    key={n.id}
                    className="border-t hover:bg-gray-50 cursor-pointer"
                    onClick={() => openDrawer(n)}
                  >
                    <td className="p-3">{n.ncr_number || n.id}</td>
                    <td className="p-3">{n.title || "-"}</td>
                    <td className="p-3">{n.severity || "-"}</td>
                    <td className="p-3">
                      <Pill text={n.status} />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span>{n.due_date || "-"}</span>
                        {n.overdue ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                            Overdue {n.days_overdue}d
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="p-3">
                      {n.inspection_number || n.inspection_id || "-"}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        disabled={Number(pdfBusyId) === Number(n.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          openPdfPreview(n.id, n.ncr_number || `NCR-${n.id}`);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {Number(pdfBusyId) === Number(n.id) ? "Loading" : "PDF"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pdfPreviewUrl && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3 md:p-6">
          <div className="flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <div>
                <div className="font-semibold text-gray-900">{pdfPreviewLabel || "NCR Report"}</div>
                <div className="text-xs text-gray-500">A4 NCR report preview</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadPdf(pdfPreviewNcrId, pdfPreviewLabel || "NCR")}
                  disabled={!pdfPreviewNcrId}
                  className="inline-flex items-center gap-2 rounded-md bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" /> Download
                </button>
                <button type="button" onClick={closePdfPreview} className="rounded-md p-2 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <iframe title="NCR PDF preview" src={pdfPreviewUrl} className="h-full w-full bg-gray-100" />
          </div>
        </div>
      )}

      {openId && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={closeDrawer} />
          <div className="w-full max-w-xl bg-white h-full shadow-xl overflow-y-auto">
            <div className="p-4 border-b flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {detail?.ncr_number || `NCR #${openId}`}
                  </h3>
                  <Pill text={detail?.status} />
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {detail?.title || "-"}
                </p>
                <div className="text-xs text-gray-500 mt-1">
                  {activeProject?.name || `Project #${projectId}`} • Severity:{" "}
                  {detail?.severity || "-"} • Issued:{" "}
                  {detail?.issued_date || "-"}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {allowEdit && !editing ? (
                  <button
                    type="button"
                    onClick={startEditing}
                    className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={Number(pdfBusyId) === Number(openId)}
                  onClick={() => openPdfPreview(openId, detail?.ncr_number || `NCR-${openId}`)}
                  className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50"
                >
                  <FileText className="h-3.5 w-3.5" /> Preview
                </button>
                <button
                  type="button"
                  disabled={Number(pdfBusyId) === Number(openId)}
                  onClick={() => downloadPdf(openId, detail?.ncr_number || `NCR-${openId}`)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
                <button
                  onClick={closeDrawer}
                  className="p-2 rounded hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-6">
              {detailLoading && (
                <div className="text-sm text-gray-500">Loading detail...</div>
              )}

              {editing ? (
                <div className="rounded-xl border border-blue-100 bg-blue-50/30 p-1">
                  <NCRForm
                    mode="edit"
                    value={editForm}
                    onChange={(field, value) =>
                      setEditForm((current) => ({
                        ...current,
                        [field]: value,
                      }))
                    }
                    onSubmit={submitEdit}
                    onCancel={() => setEditing(false)}
                    creating={actionBusy}
                    inspections={inspections}
                    inspectionsLoading={inspectionsLoading}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-gray-800">
                    Summary
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-line">
                    {detail?.description || "-"}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                    <div>
                      <div className="font-medium text-gray-700">Location</div>
                      <div>{detail?.location || "-"}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">Inspection</div>
                      <div>
                        {detail?.inspection_number || detail?.inspection_id || "-"}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">Assigned To</div>
                      <div>{assignedToLabel}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">Contractor</div>
                      <div>{detail?.contractor || "-"}</div>
                    </div>
                  </div>
                </div>
              )}

              {(detail?.contractor_response ||
                detail?.resolution_description) && (
                <div className="border rounded-lg p-3 space-y-1 bg-gray-50">
                  <div className="text-sm font-medium text-gray-800">
                    Latest Assigned Person Response
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-line">
                    {detail?.contractor_response ||
                      detail?.resolution_description}
                  </div>
                  {detail?.contractor_response_date && (
                    <div className="text-xs text-gray-500">
                      Submitted: {detail.contractor_response_date}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">{noteLocked}</div>
                </div>
              )}

              {isStatus(detail?.status, "rejected") && (
                <div className="border rounded-lg p-3 space-y-1 bg-red-50">
                  <div className="text-sm font-medium text-red-800">
                    QA/QC Rejected
                  </div>
                  <div className="text-sm text-red-700 whitespace-pre-line">
                    {detail?.qa_review_comments || "-"}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="text-sm font-semibold text-gray-800">
                  Workflow
                </div>

                {actionError && (
                  <div className="text-sm text-red-600">
                    Action failed. Check permissions / backend logs.
                  </div>
                )}

                {canAssign(detail?.status, projectRole, systemRole) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <UserCheck className="w-4 h-4" /> Assign NCR
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Assign to (Project Team)
                      </label>

                      {teamLoading ? (
                        <div className="text-sm text-gray-500">
                          Loading team...
                        </div>
                      ) : (
                        <SearchableTeamMemberSelect
                          options={(teamMembers || []).filter(
                            (member) =>
                              member.is_active !== false &&
                              NCR_ASSIGNABLE_ROLES.has(
                                String(member.role || "").toLowerCase(),
                              ),
                          )}
                          value={assignPayload.assigned_to_user_id}
                          onChange={(userId) =>
                            setAssignPayload((p) => ({
                              ...p,
                              assigned_to_user_id: userId,
                            }))
                          }
                          getLabel={getUserLabelFromTeam}
                        />
                      )}

                      <div className="mt-1 text-xs text-gray-500">
                        Only Supervisors and Operations Managers can be assigned.
                      </div>

                      {teamError && (
                        <div className="text-xs text-red-600 mt-1">
                          Failed to load project team.
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        className="border rounded px-2 py-2 text-sm"
                        value={assignPayload.due_date}
                        onChange={(e) =>
                          setAssignPayload((p) => ({
                            ...p,
                            due_date: e.target.value,
                          }))
                        }
                      />
                      <input
                        className="border rounded px-2 py-2 text-sm"
                        placeholder="Company / contractor name (optional)"
                        value={assignPayload.contractor}
                        onChange={(e) =>
                          setAssignPayload((p) => ({
                            ...p,
                            contractor: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <button
                      disabled={
                        actionBusy ||
                        !assignPayload.assigned_to_user_id ||
                        !assignPayload.due_date
                      }
                      onClick={() =>
                        doAction(() =>
                          ncrAPI.assign(projectId, openId, {
                            assigned_to: assignPayload.assigned_to || null,
                            contractor: assignPayload.contractor || null,
                            assigned_to_user_id: Number(
                              assignPayload.assigned_to_user_id,
                            ),
                            due_date: assignPayload.due_date || null,
                          }),
                        )
                      }
                      className={[
                        "inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition",
                        "bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400",
                      ].join(" ")}
                    >
                      <UserCheck className="w-4 h-4" /> Assign
                    </button>

                    {!assignPayload.assigned_to_user_id && (
                      <div className="text-xs text-gray-500">
                        Select an eligible Supervisor or Operations Manager to
                        enable Assign.
                      </div>
                    )}
                  </div>
                )}

                {(isAssignedCurrentUser &&
                  NCR_ESCALATION_ROLES.has(escalationRole)) ||
                openEscalationToMe ? (
                  <div className="border rounded-lg p-3 space-y-3 bg-orange-50/40">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <ArrowUpRight className="w-4 h-4 text-orange-600" />
                        {openEscalationToMe
                          ? "Forward NCR Escalation"
                          : "Escalate NCR"}
                      </div>
                      <span className="text-xs text-gray-500">
                        Published reporting path only
                      </span>
                    </div>
                    <select
                      className="w-full border rounded px-2 py-2 text-sm bg-white"
                      value={escalationTargetUserId}
                      onChange={(e) =>
                        setEscalationTargetUserId(e.target.value)
                      }
                    >
                      <option value="">Select escalation target</option>
                      {escalationTargets.map((target) => (
                        <option key={target.user_id} value={target.user_id}>
                          {target.name} —{" "}
                          {String(target.role || "").replaceAll("_", " ")}
                        </option>
                      ))}
                    </select>
                    <textarea
                      className="w-full border rounded px-2 py-2 text-sm"
                      rows={3}
                      placeholder="Why is technical or management escalation required?"
                      value={escalationReason}
                      onChange={(e) => setEscalationReason(e.target.value)}
                    />
                    <button
                      disabled={
                        actionBusy ||
                        !escalationTargetUserId ||
                        !escalationReason.trim()
                      }
                      onClick={() =>
                        doEscalationAction(() =>
                          openEscalationToMe
                            ? ncrAPI.forwardEscalation(
                                projectId,
                                openEscalationToMe.id,
                                {
                                  escalated_to_user_id: Number(
                                    escalationTargetUserId,
                                  ),
                                  reason: escalationReason.trim(),
                                },
                              )
                            : ncrAPI.escalate(projectId, openId, {
                                escalated_to_user_id: Number(
                                  escalationTargetUserId,
                                ),
                                reason: escalationReason.trim(),
                              }),
                        )
                      }
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      <ArrowUpRight className="w-4 h-4" />{" "}
                      {openEscalationToMe ? "Forward Escalation" : "Escalate"}
                    </button>
                  </div>
                ) : null}

                {openEscalationToMe ? (
                  <div className="border rounded-lg p-3 space-y-2 bg-blue-50/40">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <Send className="w-4 h-4 text-blue-600" /> Answer
                      Escalation
                    </div>
                    <div className="text-xs text-gray-600 whitespace-pre-line">
                      Reason: {openEscalationToMe.reason}
                    </div>
                    <textarea
                      className="w-full border rounded px-2 py-2 text-sm"
                      rows={3}
                      placeholder="Provide technical guidance or management direction..."
                      value={escalationAnswer}
                      onChange={(e) => setEscalationAnswer(e.target.value)}
                    />
                    <button
                      disabled={actionBusy || !escalationAnswer.trim()}
                      onClick={() =>
                        doEscalationAction(() =>
                          ncrAPI.answerEscalation(
                            projectId,
                            openEscalationToMe.id,
                            { response_text: escalationAnswer.trim() },
                          ),
                        )
                      }
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      <Send className="w-4 h-4" /> Answer
                    </button>
                  </div>
                ) : null}

                {canRespond(
                  detail,
                  projectRole,
                  systemRole,
                  currentUser?.id,
                ) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <Send className="w-4 h-4" /> Assigned Person Response
                    </div>

                    <div className="text-xs text-gray-500">{noteLocked}</div>

                    <textarea
                      className="w-full border rounded px-2 py-1 text-sm"
                      rows={4}
                      placeholder="Write the corrective action / evidence. Once submitted, it can’t be edited."
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                    />
                    <button
                      disabled={actionBusy || !responseText.trim()}
                      onClick={() =>
                        doAction(() =>
                          ncrAPI.respond(projectId, openId, {
                            contractor_response: responseText.trim(),
                          }),
                        )
                      }
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      <Send className="w-4 h-4" /> Submit Response
                    </button>
                  </div>
                )}

                {canResubmit(
                  detail,
                  projectRole,
                  systemRole,
                  currentUser?.id,
                ) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <Send className="w-4 h-4" /> Resubmit Response
                    </div>

                    <div className="text-xs text-gray-500">
                      Note: this updates the latest response, but the previous
                      response remains saved in the NCR timeline.
                    </div>

                    <textarea
                      className="w-full border rounded px-2 py-1 text-sm"
                      rows={4}
                      placeholder="Write the updated corrective action / evidence. Once resubmitted, it can’t be edited."
                      value={resubmitText}
                      onChange={(e) => setResubmitText(e.target.value)}
                    />
                    <button
                      disabled={actionBusy || !resubmitText.trim()}
                      onClick={() =>
                        doAction(() =>
                          ncrAPI.resubmit(projectId, openId, {
                            contractor_response: resubmitText.trim(),
                          }),
                        )
                      }
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      <Send className="w-4 h-4" /> Resubmit
                    </button>
                  </div>
                )}

                {canStartReview(detail?.status, projectRole, systemRole) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <ClipboardCheck className="w-4 h-4" /> QA/QC Review
                    </div>

                    <button
                      disabled={actionBusy}
                      onClick={() =>
                        doAction(() => ncrAPI.review(projectId, openId))
                      }
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      <ClipboardCheck className="w-4 h-4" /> Start Review
                    </button>

                    <div className="text-xs text-gray-500">
                      Start review first. Reject/Close will appear when status
                      becomes <b>qa_review</b>.
                    </div>
                  </div>
                )}

                {canReject(detail?.status, projectRole, systemRole) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <AlertCircle className="w-4 h-4" /> Reject NCR
                    </div>

                    <textarea
                      className="w-full border rounded px-2 py-1 text-sm"
                      rows={3}
                      placeholder="Write rejection reason (required). This will be shown to the assigned person."
                      value={qaReviewComments}
                      onChange={(e) => setQaReviewComments(e.target.value)}
                    />

                    <button
                      disabled={actionBusy || !qaReviewComments.trim()}
                      onClick={() =>
                        doAction(() =>
                          ncrAPI.reject(projectId, openId, {
                            qa_review_comments: qaReviewComments.trim(),
                          }),
                        )
                      }
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      Reject
                    </button>
                  </div>
                )}

                {canClose(detail?.status, projectRole, systemRole) && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <BadgeCheck className="w-4 h-4" /> Verify & Close
                    </div>

                    <textarea
                      className="w-full border rounded px-2 py-1 text-sm"
                      rows={3}
                      placeholder="Verification notes (optional)"
                      value={verificationNotes}
                      onChange={(e) => setVerificationNotes(e.target.value)}
                    />

                    <button
                      disabled={actionBusy}
                      onClick={() =>
                        doAction(() =>
                          ncrAPI.close(projectId, openId, {
                            verification_notes:
                              verificationNotes?.trim() || null,
                          }),
                        )
                      }
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      <BadgeCheck className="w-4 h-4" /> Close NCR
                    </button>
                  </div>
                )}

                {isAdmin(systemRole) && (
                  <div className="text-xs text-gray-500">
                    Admin note: assigned-person responses are locked in the NCR
                    timeline.
                  </div>
                )}
              </div>

              {ncrEscalations.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <History className="w-4 h-4" /> Escalation Chain
                  </div>
                  <div className="space-y-2">
                    {ncrEscalations.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-lg border bg-gray-50 p-3 text-sm"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium text-gray-800">
                            {item.escalated_by_name ||
                              `User #${item.escalated_by_user_id}`}{" "}
                            →{" "}
                            {item.escalated_to_name ||
                              `User #${item.escalated_to_user_id}`}
                          </span>
                          <Pill text={item.status} />
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {String(item.from_role || "").replaceAll("_", " ")} →{" "}
                          {String(item.to_role || "").replaceAll("_", " ")}
                        </div>
                        <div className="mt-2 whitespace-pre-line text-gray-700">
                          {item.reason}
                        </div>
                        {item.response_text ? (
                          <div className="mt-2 rounded bg-blue-50 p-2 text-blue-800">
                            Response: {item.response_text}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="space-y-2">
                <div className="text-sm font-semibold text-gray-800">
                  Timeline
                </div>

                {timelineLoading && (
                  <div className="text-sm text-gray-500">
                    Loading timeline...
                  </div>
                )}
                {timelineError && (
                  <div className="text-sm text-red-600">
                    Failed to load timeline.
                  </div>
                )}

                {!timelineLoading &&
                  !timelineError &&
                  (timeline || []).length === 0 && (
                    <div className="text-sm text-gray-500">
                      No actions recorded yet.
                    </div>
                  )}

                {!timelineLoading &&
                  !timelineError &&
                  (timeline || []).length > 0 && (
                    <div className="space-y-2">
                      {timeline.map((t) => (
                        <div
                          key={t.id || `${t.action_date}-${t.action_taken}`}
                          className="border rounded-md p-3 flex items-start justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">
                              {t.action_date || "-"} • {t.taken_by || "-"}
                              {t.previous_status || t.new_status ? (
                                <>
                                  {" "}
                                  • {t.previous_status || "-"} →{" "}
                                  {t.new_status || "-"}
                                </>
                              ) : null}
                            </div>
                            <div className="text-sm text-gray-800 mt-1 truncate max-w-[360px]">
                              {t.message || t.action_taken || "-"}
                            </div>
                          </div>

                          <button
                            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md border hover:bg-gray-50"
                            title="View message"
                            onClick={() => {
                              setMsgItem(t);
                              setMsgOpen(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              {msgOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                  <div
                    className="absolute inset-0 bg-black/30"
                    onClick={() => {
                      setMsgOpen(false);
                      setMsgItem(null);
                    }}
                  />
                  <div className="relative w-full max-w-xl bg-white rounded-lg shadow-xl">
                    <div className="px-4 py-3 border-b flex items-start justify-between gap-4">
                      <div>
                        <div className="font-semibold text-gray-900">
                          Timeline message
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {msgItem?.action_date || "-"} •{" "}
                          {msgItem?.taken_by || "-"}
                          {msgItem?.previous_status || msgItem?.new_status ? (
                            <>
                              {" "}
                              • {msgItem?.previous_status || "-"} →{" "}
                              {msgItem?.new_status || "-"}
                            </>
                          ) : null}
                        </div>
                      </div>
                      <button
                        className="p-2 rounded hover:bg-gray-100"
                        onClick={() => {
                          setMsgOpen(false);
                          setMsgItem(null);
                        }}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="p-4">
                      <div className="text-sm text-gray-800 whitespace-pre-line">
                        {msgItem?.message || msgItem?.action_taken || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NCRView;
